/**
 * Bundle 97 - Client-side canvas image resize helper.
 *
 * Called by the event-editor uploaders BEFORE building FormData.
 * If the file is above the size / dimension threshold, resize to
 * 2400px longest edge JPEG q=0.85. This defensively caps upload
 * bytes at ~1.5MB for the vast majority of iPhone photos so
 * `/api/media/upload/v2` never sees a large body.
 *
 * Returns the original file if it is already small enough or if
 * canvas / createImageBitmap are unavailable (server-rendered
 * fallback).
 */
const SIZE_THRESHOLD_BYTES = 4 * 1024 * 1024;   // 4 MB
const DIM_THRESHOLD_PX = 3200;
const TARGET_LONGEST_EDGE = 2400;
const JPEG_QUALITY = 0.85;


const _needsResize = (file, width, height) => {
  if (file.size > SIZE_THRESHOLD_BYTES) return true;
  if (width > DIM_THRESHOLD_PX || height > DIM_THRESHOLD_PX) return true;
  return false;
};


export const resizeImageIfNeeded = async (file) => {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return file;
  }
  // GIF and WEBP-animated skip resize (canvas cannot preserve
  // animation). Small PNGs / JPGs also skip.
  if (file.type === 'image/gif') return file;
  if (typeof createImageBitmap !== 'function') return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const { width, height } = bitmap;
  if (!_needsResize(file, width, height)) {
    bitmap.close?.();
    return file;
  }
  const scale = TARGET_LONGEST_EDGE / Math.max(width, height);
  const nw = Math.round(width * scale);
  const nh = Math.round(height * scale);
  let canvas;
  if (typeof OffscreenCanvas === 'function') {
    canvas = new OffscreenCanvas(nw, nh);
  } else {
    canvas = document.createElement('canvas');
    canvas.width = nw;
    canvas.height = nh;
  }
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, nw, nh);
  bitmap.close?.();
  let blob;
  if (typeof canvas.convertToBlob === 'function') {
    blob = await canvas.convertToBlob({
      type: 'image/jpeg', quality: JPEG_QUALITY,
    });
  } else {
    blob = await new Promise((resolve) => canvas.toBlob(
      resolve, 'image/jpeg', JPEG_QUALITY,
    ));
  }
  if (!blob) return file;
  // Preserve the original filename so ops logs stay readable.
  const base = (file.name || 'upload').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

export const _internals = {
  SIZE_THRESHOLD_BYTES,
  DIM_THRESHOLD_PX,
  TARGET_LONGEST_EDGE,
  JPEG_QUALITY,
};
